/**
 * Oziqlanish termik holati va issiqlik balansi.
 *
 * MANBA: Kasatkin, gl. XVII ("Тепловой баланс ректификационной колонны");
 *        Pavlov, Romankov, Noskov, gl. 7 (misol 7.3);
 *        McCabe, Smith, Harriott, Ch. 21 (q-line, eq. 21.26–21.30).
 *
 * TENGLAMALAR (J/mol, mol/s → W):
 *   q ≡ (H_V − H_F)/(H_V − H_L)  — oziqlanish bosqichidagi to'yingan fazalar entalpiyasi.
 *     TF ≤ Tbub:        q = 1 + cpL·(Tbub − TF)/λF          (o'ta sovutilgan suyuqlik)
 *     Tbub < TF < Tdew: q = (Tdew − TF)/(Tdew − Tbub)       (ikki fazali, chiziqli)
 *     TF ≥ Tdew:        q = −cpV·(TF − Tdew)/λF              (o'ta qizigan bug')
 *   Suyuqlik entalpiyasi (273.15 K dan): hL(x, T) = Σ xi·Mi·∫cp_i dT
 *   Bug'lanish issiqligi:              λ(x, T) = Σ xi·Mi·r_i(T)
 *   Kondensator (to'liq, sovutmasdan): Qc = V·λ(xD, Ttop),  V = (R+1)·D
 *   Qaynatgich (umumiy balans):        Qr = D·hD + W·hW + Qc − F·hF,
 *                                      hF = hL(xF, Tbub,F) + (1 − q)·λ(xF, Tbub,F)
 *
 * ASSUMPTION:
 *   1. Aralash entalpiyasi (heat of mixing) = 0; komponent entalpiyalari mol ulushi bo'yicha
 *      qo'shiladi.
 *   2. Ikki fazali oziqlanish uchun q chiziqli interpolyatsiya (aniq flash hisobi yo'q).
 *   3. Kolonnadan atrofga issiqlik yo'qotish yo'q.
 *   4. Distillyat va qoldiq to'yingan suyuqlik holida chiqadi (sovutish yo'q).
 */
import {
  enthalpyChange,
  getSubstance,
  heatOfVaporization,
  meanHeatCapacity,
} from '../../properties';
import type { WarningSink } from '../../types';
import type { BinaryVle } from './vle';

const T_REF = 273.15;

export interface MixtureProps {
  light: string;
  heavy: string;
  /** kg/mol */
  ML: number;
  MH: number;
}

export function mixtureProps(light: string, heavy: string): MixtureProps {
  return { light, heavy, ML: getSubstance(light).molarMass, MH: getSubstance(heavy).molarMass };
}

/** Molyar bug'lanish issiqligi, J/mol */
export function molarHeatOfVaporization(
  m: MixtureProps,
  x: number,
  T: number,
  warnings?: WarningSink,
): number {
  return (
    x * m.ML * heatOfVaporization(m.light, T, warnings) +
    (1 - x) * m.MH * heatOfVaporization(m.heavy, T, warnings)
  );
}

/** Suyuqlik molyar entalpiyasi 273.15 K ga nisbatan, J/mol */
export function molarLiquidEnthalpy(
  m: MixtureProps,
  x: number,
  T: number,
  warnings?: WarningSink,
): number {
  return (
    x * m.ML * enthalpyChange(m.light, 'liquid', T_REF, T, warnings) +
    (1 - x) * m.MH * enthalpyChange(m.heavy, 'liquid', T_REF, T, warnings)
  );
}

export interface FeedThermalState {
  q: number;
  /** Oziqlanish qaynash harorati, K */
  Tbub: number;
  Tdew: number;
}

/** Oziqlanish haroratidan q */
export function feedQualityFromTemperature(
  vle: BinaryVle,
  m: MixtureProps,
  xF: number,
  TF: number,
  warnings?: WarningSink,
): FeedThermalState {
  const Tbub = vle.bubbleT(xF);
  const Tdew = vle.dewT(xF);
  if (TF <= Tbub) {
    const cpL =
      xF * m.ML * meanHeatCapacity(m.light, 'liquid', TF, Tbub, warnings) +
      (1 - xF) * m.MH * meanHeatCapacity(m.heavy, 'liquid', TF, Tbub, warnings);
    const lambda = molarHeatOfVaporization(m, xF, Tbub, warnings);
    return { q: 1 + (cpL * (Tbub - TF)) / lambda, Tbub, Tdew };
  }
  if (TF >= Tdew) {
    const cpV =
      xF * m.ML * meanHeatCapacity(m.light, 'gas', Tdew, TF, warnings) +
      (1 - xF) * m.MH * meanHeatCapacity(m.heavy, 'gas', Tdew, TF, warnings);
    const lambda = molarHeatOfVaporization(m, xF, Tdew, warnings);
    return { q: -(cpV * (TF - Tdew)) / lambda, Tbub, Tdew };
  }
  return { q: (Tdew - TF) / (Tdew - Tbub), Tbub, Tdew };
}

export interface HeatDuties {
  /** W */
  Qcondenser: number;
  Qreboiler: number;
}

export function heatDuties(
  m: MixtureProps,
  args: {
    F: number;
    D: number;
    W: number;
    R: number;
    q: number;
    xF: number;
    xD: number;
    xW: number;
    Ttop: number;
    Tbottom: number;
    Tfeed: number;
  },
  warnings?: WarningSink,
): HeatDuties {
  const V = (args.R + 1) * args.D;
  const Qcondenser = V * molarHeatOfVaporization(m, args.xD, args.Ttop, warnings);
  const hD = molarLiquidEnthalpy(m, args.xD, args.Ttop, warnings);
  const hW = molarLiquidEnthalpy(m, args.xW, args.Tbottom, warnings);
  const hF =
    molarLiquidEnthalpy(m, args.xF, args.Tfeed, warnings) +
    (1 - args.q) * molarHeatOfVaporization(m, args.xF, args.Tfeed, warnings);
  const Qreboiler = args.D * hD + args.W * hW + Qcondenser - args.F * hF;
  return { Qcondenser, Qreboiler };
}
